import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { History, Undo2, Settings, RefreshCw, Loader2, AlertTriangle, BookOpen, Pill, Stethoscope, Beaker, Users, Video, Phone, FileText, Layers } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import SandboxBanner from "@/components/SandboxBanner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ACTION_META = {
  create: { color: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40", label: "Created" },
  update: { color: "bg-cyan-500/15 text-cyan-300 border-cyan-500/40", label: "Updated" },
  delete: { color: "bg-red-500/15 text-red-300 border-red-500/40", label: "Deleted" },
  revert: { color: "bg-amber-500/15 text-amber-300 border-amber-500/40", label: "Reverted" },
};

const ENTITY_META = {
  drug_concentration:  { icon: Beaker,      label: "Drug Concentration",  color: "text-purple-400" },
  protocol:            { icon: BookOpen,    label: "Protocol",             color: "text-slate-300" },
  protocol_references: { icon: FileText,    label: "Protocol References",  color: "text-indigo-400" },
  drug:                { icon: Pill,        label: "Medication",           color: "text-green-400" },
  procedure:           { icon: Stethoscope, label: "Procedure",            color: "text-amber-400" },
  user:                { icon: Users,       label: "User",                 color: "text-pink-400" },
  video:               { icon: Video,       label: "Video",                color: "text-cyan-400" },
  phonebook:           { icon: Phone,       label: "Phone Book",           color: "text-rose-400" },
};

const ENTITY_FILTERS = [
  { value: "all", label: "All", icon: Layers },
  { value: "drug_concentration", label: "Concentrations", icon: Beaker },
  { value: "protocol", label: "Protocols", icon: BookOpen },
  { value: "drug", label: "Medications", icon: Pill },
  { value: "procedure", label: "Procedures", icon: Stethoscope },
  { value: "user", label: "Users", icon: Users },
  { value: "video", label: "Videos", icon: Video },
  { value: "phonebook", label: "Phone Book", icon: Phone },
];

function fmtTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`;
}

// Deep-but-shallow JSON diff summary — surfaces the keys that changed
function summariseDiff(before, after) {
  if (!before && after) return "First version created";
  if (before && !after) return "Document deleted";
  if (!before && !after) return "—";
  const changes = [];
  const keys = new Set([...Object.keys(before || {}), ...Object.keys(after || {})]);
  for (const k of keys) {
    if (k === "drug" || k === "_id") continue;
    const a = JSON.stringify(before?.[k] ?? null);
    const b = JSON.stringify(after?.[k] ?? null);
    if (a !== b) changes.push(k);
  }
  return changes.length ? `Changed: ${changes.join(", ")}` : "No field-level changes";
}

const AdminAudit = () => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reverting, setReverting] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [entityFilter, setEntityFilter] = useState("all");
  const { token, isAdmin, isDemoAdmin } = useAuth();

  const fetchEntries = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: "200" });
      if (entityFilter !== "all") params.set("entity_type", entityFilter);
      const res = await axios.get(`${API}/admin/audit?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setEntries(res.data || []);
    } catch (e) {
      toast.error("Failed to load audit log");
    } finally {
      setLoading(false);
    }
  }, [token, entityFilter]);

  useEffect(() => { fetchEntries(); }, [fetchEntries]);

  const handleRevert = async (entry) => {
    if (!window.confirm(
      `Revert ${entry.action.toUpperCase()} on "${entry.entity_name}" from ${fmtTime(entry.created_at)}?\n\nThis will restore the previous values to the live database.`
    )) return;
    setReverting(entry.id);
    try {
      await axios.post(`${API}/admin/audit/${entry.id}/revert`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`Reverted ${entry.action} on ${entry.entity_name}`);
      fetchEntries();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Revert failed";
      // SANDBOX_BLOCKED is silently handled by the global interceptor
      if (!String(msg).startsWith("SANDBOX_BLOCKED")) toast.error(msg);
    } finally {
      setReverting(null);
    }
  };

  return (
    <div className="container mx-auto py-6 px-4 max-w-4xl">
      <SandboxBanner />

      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-heading font-bold flex items-center gap-2">
            <History className="w-6 h-6 text-cyan-400" />
            Admin Audit Log
          </h1>
          <p className="text-sm text-muted-foreground">Every admin write is logged with before/after snapshots. Tap Undo to revert any entry.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={fetchEntries} disabled={loading} data-testid="audit-refresh">
            <RefreshCw className={`w-4 h-4 mr-1.5 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
          <Link to="/admin/dashboard">
            <Button variant="outline" size="sm">
              <Settings className="w-4 h-4 mr-2" />
              Dashboard
            </Button>
          </Link>
        </div>
      </div>

      {/* Entity-type filter chips */}
      <div className="flex gap-1.5 flex-wrap mb-3" data-testid="audit-filter-row">
        {ENTITY_FILTERS.map((f) => {
          const Icon = f.icon;
          const active = entityFilter === f.value;
          return (
            <Button
              key={f.value}
              size="sm"
              variant={active ? "default" : "outline"}
              onClick={() => setEntityFilter(f.value)}
              className="h-7 text-xs gap-1"
              data-testid={`audit-filter-${f.value}`}
            >
              <Icon className="w-3 h-3" />
              {f.label}
            </Button>
          );
        })}
      </div>

      {loading ? (
        <div className="text-center py-12 text-muted-foreground">
          <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
          Loading audit log…
        </div>
      ) : entries.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            No audit entries yet for this filter.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2" data-testid="audit-entries">
          {entries.map((entry) => {
            const meta = ACTION_META[entry.action] || ACTION_META.update;
            const entityMeta = ENTITY_META[entry.entity_type] || { icon: Layers, label: entry.entity_type, color: "text-muted-foreground" };
            const EntityIcon = entityMeta.icon;
            const isExpanded = expanded === entry.id;
            const isVideoEntry = entry.entity_type === "video";
            const canRevert = isAdmin && !entry.reverted && entry.action !== "revert" && !isVideoEntry;
            return (
              <Card key={entry.id} className={entry.reverted ? "opacity-60" : ""} data-testid={`audit-entry-${entry.id}`}>
                <CardContent className="py-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge variant="outline" className={meta.color} data-testid={`audit-action-${entry.action}`}>
                          {meta.label}
                        </Badge>
                        <Badge variant="outline" className={`gap-1 ${entityMeta.color} border-current/40`} data-testid={`audit-entity-${entry.entity_type}`}>
                          <EntityIcon className="w-3 h-3" />
                          {entityMeta.label}
                        </Badge>
                        <span className="font-mono text-sm font-semibold capitalize truncate">{entry.entity_name}</span>
                        {entry.reverted && (
                          <Badge variant="outline" className="text-amber-400 border-amber-500/40 gap-1">
                            <Undo2 className="w-3 h-3" />Reverted
                          </Badge>
                        )}
                        {isVideoEntry && (
                          <Badge variant="outline" className="text-muted-foreground text-[9px]">read-only</Badge>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground mt-1">
                        {fmtTime(entry.created_at)} · by <span className="font-medium">{entry.admin_email}</span>
                      </div>
                      <div className="text-xs mt-1.5">{summariseDiff(entry.before, entry.after)}</div>
                      {isExpanded && (
                        <div className="mt-2 grid sm:grid-cols-2 gap-2 text-[11px] font-mono" data-testid={`audit-diff-${entry.id}`}>
                          <div className="rounded border border-border bg-secondary/20 p-2 overflow-auto max-h-48">
                            <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Before</div>
                            <pre className="whitespace-pre-wrap break-all">{entry.before ? JSON.stringify(entry.before, null, 2) : "—"}</pre>
                          </div>
                          <div className="rounded border border-border bg-secondary/20 p-2 overflow-auto max-h-48">
                            <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">After</div>
                            <pre className="whitespace-pre-wrap break-all">{entry.after ? JSON.stringify(entry.after, null, 2) : "—"}</pre>
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col gap-1 shrink-0">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setExpanded(isExpanded ? null : entry.id)}
                        className="h-7 text-xs"
                        data-testid={`audit-toggle-${entry.id}`}
                      >
                        {isExpanded ? "Hide" : "Diff"}
                      </Button>
                      {canRevert && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleRevert(entry)}
                          disabled={reverting === entry.id}
                          className="h-7 text-xs gap-1 border-amber-500/40 text-amber-400 hover:bg-amber-500/10"
                          data-testid={`audit-revert-${entry.id}`}
                        >
                          {reverting === entry.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Undo2 className="w-3 h-3" />}
                          Undo
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {isDemoAdmin && (
        <p className="text-xs text-muted-foreground italic mt-4 flex items-center gap-1.5">
          <AlertTriangle className="w-3.5 h-3.5 text-amber-400" />
          Sandbox accounts can view audit history but cannot revert.
        </p>
      )}
    </div>
  );
};

export default AdminAudit;
